// Игровой цикл: прогресс, фазы, расчёт, тайминги.
// Вынесено из api.ts (Фаза 1, разбор клиента по доменам).

import { request, LONG_ACTION_TIMEOUT_MS } from "./client";
import { type CycleAction } from "./action";
import { type CampaignRosterEvent, type ChronicleChapter, type ChronicleEvent, type WorldChange } from "./chronicle";
import { type CycleParticipant, type CyclePhase } from "./common";
import { type DiscussionRoom, type NegotiationAgreement, type NpcVisit } from "./discussion";
import { type Initiative, type InitiativeResolution } from "./initiative";
import { type MediaAsset } from "./media";

export interface CycleMemberProgress extends CycleParticipant {
  actionsUsed: number;
  actionLimit: number;
  dayReady: boolean;
  hasInitiative: boolean;
  skippedInitiative: boolean;
  agendaReady: boolean;
}
export interface DaySummary {
  actionsCompleted: number;
  successfulActions: number;
  agreementsCreated: number;
  intelligenceFindings: number;
  initiativePreparationBonus: number;
  pendingDeferredResults: number;
  resolvedDeferredResults: number;
}
export interface CampaignTimingSettings {
  dayDurationMinutes: number;
  closingDurationMinutes: number;
  votingDurationMinutes: number;
  npcVisitDurationMinutes: number;
}
export interface CycleUnread {
  totalCount: number;
  chronicleCount: number;
  chatCount: number;
  initiativeCount: number;
}
export type ReadScope = "CHRONICLE" | "INITIATIVES" | "ROOM";
export interface GameCycle {
  id: string;
  cycleNumber: number;
  title: string;
  phase: CyclePhase;
  situationSummary?: string;
  centralProblem?: string;
  complications: string[];
  privateBriefing?: string;
  openingImages: MediaAsset[];
  closingNarrative?: string;
  closingImages: MediaAsset[];
  chronicleComicRecapImages: MediaAsset[];
  generationProvider?: string;
  openedAt?: string;
  dayDeadlineAt?: string;
  closingDeadlineAt?: string;
  votingDeadlineAt?: string;
  automaticTransitionReason?: string;
  memberCount: number;
  dayReadyCount: number;
  agendaReadyCount: number;
  actionLimit: number;
  actionsUsed: number;
  canAct: boolean;
  canMarkDayReady: boolean;
  canOpenAgenda: boolean;
  canEditInitiative: boolean;
  canMarkAgendaReady: boolean;
  canOpenVoting: boolean;
  canResolve: boolean;
  canForceCompletePhase: boolean;
  forceCompletePhaseLabel?: string;
  canStartNextCycle: boolean;
  myDayReady: boolean;
  myAgendaReady: boolean;
  mySkippedInitiative: boolean;
  myInitiativeId?: string;
  unread: CycleUnread;
  daySummary: DaySummary;
  memberProgress: CycleMemberProgress[];
  actions: CycleAction[];
  rooms: DiscussionRoom[];
  npcVisits: NpcVisit[];
  agreements: NegotiationAgreement[];
  initiatives: Initiative[];
  resolutions: InitiativeResolution[];
  chronicleEvents: ChronicleEvent[];
  worldChanges: WorldChange[];
}
export interface CampaignCycleOverview {
  current?: GameCycle;
  canStartCycle: boolean;
  timingSettings: CampaignTimingSettings;
  chronicleChapters: ChronicleChapter[];
  rosterEvents: CampaignRosterEvent[];
}

export const cycleApi = {
  markRead: (campaignId: string, scope: ReadScope, scopeId?: string) =>
    request<{ scope: ReadScope; scopeId?: string; readAt: string }>(
      `/api/campaigns/${campaignId}/read-state`,
      { method: "PUT", body: JSON.stringify({ scope, scopeId }) },
    ),
  startCycle: (campaignId: string) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/start`,
      { method: "POST" },
      LONG_ACTION_TIMEOUT_MS,
    ),
  updateCycleTimingSettings: (
    campaignId: string,
    payload: CampaignTimingSettings,
  ) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/timing-settings`,
      { method: "PUT", body: JSON.stringify(payload) },
    ),
  setDayReadiness: (campaignId: string, ready: boolean) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/day-readiness`,
      { method: "POST", body: JSON.stringify({ ready }) },
      LONG_ACTION_TIMEOUT_MS,
    ),
  openCycleAgenda: (campaignId: string) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/agenda/open`,
      { method: "POST" },
      LONG_ACTION_TIMEOUT_MS,
    ),
  setAgendaReadiness: (campaignId: string, ready: boolean) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/agenda-readiness`,
      { method: "POST", body: JSON.stringify({ ready }) },
    ),
  openCycleVoting: (campaignId: string) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/voting/open`,
      { method: "POST" },
    ),
  resolveCycle: (campaignId: string) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/resolve`,
      { method: "POST" },
      LONG_ACTION_TIMEOUT_MS,
    ),
  forceCompleteCyclePhase: (campaignId: string) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/force-complete-phase`,
      { method: "POST" },
      LONG_ACTION_TIMEOUT_MS,
    ),
  regenerateClosingNarrative: (campaignId: string) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/chronicle/regenerate`,
      { method: "POST" },
      LONG_ACTION_TIMEOUT_MS,
    ),
};
